import { useEffect, useRef, useState } from "react";
import api, { apiError, API } from "@/lib/api";
import { Page, PageHeader, Empty } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Send, Save, Plus, Trash2, ExternalLink, Bot, User, Loader2, BookOpen } from "lucide-react";
import { toast } from "sonner";

const SOURCES = ["Lovdata", "NAV", "Skatteetaten", "Altinn", "Brønnøysund", "Regnskap Norge", "Revisorforeningen"];

function SourceList({ sources }) {
  if (!sources || sources.length === 0) return null;
  return (
    <div className="mt-3 border-t border-border/60 pt-3 space-y-1.5" data-testid="assistant-sources">
      <p className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5" /> Kilder</p>
      {sources.map((s, i) => (
        <a key={s.url} href={s.url} target="_blank" rel="noreferrer" data-testid={`assistant-source-${i}`}
          className="flex items-start gap-2 text-xs text-muted-foreground hover:text-foreground group">
          <span className="font-mono text-foreground/70 shrink-0">[{i + 1}]</span>
          <span className="truncate group-hover:underline">{s.title}</span>
          <ExternalLink className="w-3 h-3 shrink-0 opacity-0 group-hover:opacity-100" />
        </a>
      ))}
    </div>
  );
}

export default function Assistent() {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState([]);
  const [isSaved, setIsSaved] = useState(false);
  const endRef = useRef(null);

  const loadSaved = () => api.get("/assistant/sessions").then((r) => setSaved(r.data)).catch(() => {});
  useEffect(() => { loadSaved(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const newChat = () => { setSessionId(null); setMessages([]); setIsSaved(false); setInput(""); };

  const openSession = async (sid) => {
    try {
      const { data } = await api.get(`/assistant/sessions/${sid}/messages`);
      setSessionId(sid);
      setIsSaved(!!data.session.saved);
      setMessages(data.messages.map((m) => ({ role: m.role, content: m.content, sources: m.sources })));
    } catch (e) { toast.error(apiError(e)); }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setBusy(true);
    // legg til tom assistent-melding som fylles ut mens den strømmer
    setMessages((m) => [...m, { role: "assistant", content: "", sources: [], streaming: true }]);
    try {
      const token = localStorage.getItem("murphy_token");
      const res = await fetch(`${API}/assistant/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("Nettverksfeil");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let gotToken = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          const evt = JSON.parse(line.slice(5).trim());
          if (evt.type === "sources") {
            if (evt.session_id) setSessionId(evt.session_id);
            setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], sources: evt.sources || [] }; return c; });
          } else if (evt.type === "token") {
            gotToken = true;
            setMessages((m) => { const c = [...m]; const last = c[c.length - 1]; c[c.length - 1] = { ...last, content: last.content + evt.text }; return c; });
          } else if (evt.type === "done") {
            if (evt.session_id) setSessionId(evt.session_id);
            setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], streaming: false }; return c; });
          } else if (evt.type === "error") {
            setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: evt.message, streaming: false }; return c; });
          }
        }
      }
      if (!gotToken) {
        setMessages((m) => { const c = [...m]; if (!c[c.length - 1].content) c[c.length - 1] = { ...c[c.length - 1], content: "Beklager, noe gikk galt. Prøv igjen.", streaming: false }; return c; });
      }
    } catch (e) {
      toast.error(e.message || "Noe gikk galt");
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: "Beklager, noe gikk galt. Prøv igjen.", streaming: false }; return c; });
    } finally { setBusy(false); }
  };

  const saveChat = async () => {
    if (!sessionId) return;
    try {
      await api.post(`/assistant/sessions/${sessionId}/save`, { title: messages[0]?.content?.slice(0, 60) });
      setIsSaved(true); loadSaved(); toast.success("Samtale lagret");
    } catch (e) { toast.error(apiError(e)); }
  };

  const delSaved = async (sid, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/assistant/sessions/${sid}`);
      if (sid === sessionId) newChat();
      loadSaved(); toast.success("Slettet");
    } catch (er) { toast.error(apiError(er)); }
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <Page>
      <PageHeader title="Murphy Assistent" subtitle="Faglig assistent som svarer basert på offentlige norske kilder" testid="assistant-header">
        <Button variant="secondary" onClick={newChat} data-testid="assistant-new-chat"><Plus className="w-4 h-4 mr-1" /> Ny samtale</Button>
        {sessionId && !isSaved && messages.length > 0 && (
          <Button onClick={saveChat} data-testid="assistant-save" className="bg-white text-black hover:bg-white/90"><Save className="w-4 h-4 mr-1" /> Lagre samtale</Button>
        )}
        {isSaved && <span className="text-xs text-emerald-400 flex items-center gap-1"><Save className="w-3.5 h-3.5" /> Lagret</span>}
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-6">
        {/* Chat */}
        <div className="flex flex-col bg-card border border-border rounded-xl overflow-hidden min-h-[62vh]">
          <div className="flex-1 overflow-y-auto p-5 space-y-5" data-testid="assistant-messages">
            {messages.length === 0 && !busy && (
              <div className="h-full flex flex-col items-center justify-center text-center py-16">
                <Sparkles className="w-10 h-10 text-muted-foreground mb-4" strokeWidth={1.5} />
                <p className="font-display text-lg">Hei! Hva lurer du på?</p>
                <p className="text-muted-foreground text-sm mt-1 max-w-md">
                  Jeg svarer på regnskaps-, skatte-, lønns- og juridiske spørsmål – kun basert på kilder fra{" "}
                  {SOURCES.join(", ")}. Finner jeg ikke svar der, sier jeg tydelig ifra.
                </p>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`assistant-msg-${i}`}>
                {m.role === "assistant" && <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center shrink-0"><Bot className="w-4 h-4" /></div>}
                <div className={`max-w-[80%] rounded-xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-white text-black" : "bg-secondary"}`}>
                  {m.role === "assistant" && m.streaming && !m.content ? (
                    <span className="text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Søker i kilder og formulerer svar…</span>
                  ) : m.content}
                  {m.role === "assistant" && !m.streaming && <SourceList sources={m.sources} />}
                </div>
                {m.role === "user" && <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0"><User className="w-4 h-4" /></div>}
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div className="border-t border-border p-3 flex items-end gap-2">
            <Textarea
              value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKey}
              placeholder="Still et faglig spørsmål… (Enter for å sende)"
              rows={1} className="resize-none bg-secondary border-border min-h-[44px] max-h-32"
              data-testid="assistant-input" disabled={busy}
            />
            <Button onClick={send} disabled={busy || !input.trim()} data-testid="assistant-send" className="bg-white text-black hover:bg-white/90 shrink-0 h-11">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Lagrede samtaler */}
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-3">Lagrede samtaler</p>
          {saved.length === 0 ? (
            <p className="text-sm text-muted-foreground">Ingen lagrede samtaler ennå. Trykk «Lagre samtale» for å beholde en.</p>
          ) : (
            <div className="space-y-1.5" data-testid="assistant-saved-list">
              {saved.map((s) => (
                <button key={s.id} onClick={() => openSession(s.id)} data-testid={`assistant-saved-${s.id}`}
                  className={`w-full text-left group flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-secondary ${s.id === sessionId ? "bg-secondary" : ""}`}>
                  <span className="flex-1 truncate">{s.title}</span>
                  <Trash2 onClick={(e) => delSaved(s.id, e)} className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-red-400 shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </Page>
  );
}
